import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Chip, IconButton, Paper, TextField, MenuItem, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Icon } from '@iconify/react';
import NotificationSnackbar from '../../../components/notificationSnackbar';
import { FieldLabel, formFieldSx } from '../../../styles/formStyles';
import {
    managementCreateButtonSx,
    managementHeaderActionSx,
    managementHeaderContentSx,
    managementHeaderSx,
    managementPageSubtitleSx,
} from '../../../styles/managementStyles';
import { buttonContentSx } from '../../../styles/dialogStyles';
import { getCountries, stateLevelLabel } from '../climate/climate.geo';
import SeasonModal from './seasonModal';
import {
    ENGINE_DEFAULT_SEASONS,
    MONTHS,
    coverage,
    getSeasons,
    monthShort,
    monthsCovered,
    resetToEngineDefault,
    saveSeasons,
    spansNewYear,
    validateCalendar,
} from './seasons.service';

const cardSx = {
    p: 3,
    borderRadius: '16px',
    border: '1px solid #DFE5F6',
    backgroundColor: '#FFFFFF',
};

const captionSx = {
    fontSize: 12,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: '#625B71',
    fontWeight: 600,
};

// Enough distinct hues for a realistic calendar; beyond that it wraps, which is fine because the
// strip is read against the labels below it rather than by colour alone.
const SEASON_COLORS = ['#4F8506', '#294190', '#8A5A00', '#8E24AA', '#00796B', '#C2185B'];

function colorFor(index) {
    return SEASON_COLORS[index % SEASON_COLORS.length];
}

/**
 * The year as twelve blocks, coloured by the season that owns each month.
 *
 * This is the whole point of the screen: a calendar is correct when it tiles the year exactly, and
 * a gap or an overlap is far easier to see here than to work out from a list of month ranges.
 */
function CoverageStrip({ seasons, indexByCode }) {
    const { byMonth } = coverage(seasons);

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 0.5, mt: 2 }}>
            {MONTHS.map((month) => {
                const owners = byMonth[month.value];
                const conflict = owners.length > 1;
                const empty = owners.length === 0;
                const color = empty || conflict ? '#FFFFFF' : colorFor(indexByCode[owners[0]] ?? 0);

                return (
                    <Tooltip
                        key={month.value}
                        title={
                            empty
                                ? `${month.label}: no season`
                                : conflict
                                    ? `${month.label}: ${owners.join(' and ')}`
                                    : `${month.label}: ${owners[0]}`
                        }
                    >
                        <Box
                            sx={{
                                height: 44,
                                borderRadius: '8px',
                                backgroundColor: color,
                                border: conflict
                                    ? '2px solid #DA342E'
                                    : empty
                                        ? '2px dashed #C4C4C4'
                                        : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: empty ? '#9AA0A6' : conflict ? '#DA342E' : '#FFFFFF',
                                }}
                            >
                                {month.short}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
}

/**
 * Per-country cropping season calendars.
 *
 * The engine resolves a farmer's season from these calendars (SeasonResolver reads
 * risk.season_calendars) and falls back to the built-in India seasons for a country with none,
 * so saving here changes how farmers are scored. The alert below says so on the screen.
 */
function Seasons() {
    const [countries, setCountries] = useState([]);
    const [countryId, setCountryId] = useState('');
    const [countryNote, setCountryNote] = useState('');
    const [seasons, setSeasons] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    useEffect(() => {
        let active = true;
        (async () => {
            const { countries: list, failed } = await getCountries();
            if (!active) return;

            setCountries(list);
            setCountryId((current) => (list.some((c) => c.id === current) ? current : list[0]?.id || ''));
            if (failed) setCountryNote('Could not load the country list.');
            else if (list.length === 0) setCountryNote('No active countries are configured.');
            else setCountryNote('');
            setLoading(false);
        })();
        return () => {
            active = false;
        };
    }, []);

    const loadSeasons = useCallback(async () => {
        if (!countryId) {
            setSeasons([]);
            return;
        }
        const result = await getSeasons(countryId);
        setSeasons(result.data || []);
        setDirty(false);
    }, [countryId]);

    useEffect(() => {
        loadSeasons();
    }, [loadSeasons]);

    const indexByCode = useMemo(
        () => seasons.reduce((acc, season, index) => ({ ...acc, [season.code]: index }), {}),
        [seasons]
    );

    const issues = validateCalendar(seasons);
    const selectedCountry = countries.find((country) => country.id === countryId) || null;
    const configured = seasons.length > 0;

    const upsertSeason = (season) => {
        setSeasons((current) => {
            const without = editing
                ? current.filter((entry) => entry.code !== editing.code)
                : current;
            return [...without, season].sort((a, b) => a.startMonth - b.startMonth);
        });
        setDirty(true);
        setModalOpen(false);
        setEditing(null);
    };

    const removeSeason = (code) => {
        setSeasons((current) => current.filter((season) => season.code !== code));
        setDirty(true);
    };

    const handleSave = async () => {
        const result = await saveSeasons(countryId, seasons);
        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        setSeasons(result.data);
        setDirty(false);
        notify(`Season calendar saved for ${selectedCountry?.name || 'this country'}.`);
    };

    const handleUseDefault = async () => {
        const result = await resetToEngineDefault(countryId);
        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        setSeasons(result.data);
        setDirty(false);
        notify("Started from the engine's current calendar.");
    };

    return (
        <>
            <Box sx={managementHeaderSx}>
                <Box sx={managementHeaderContentSx}>
                    <Typography sx={managementPageSubtitleSx}>
                        The cropping calendar each country is scored against. A rainfall baseline is keyed to one
                        of these seasons.
                    </Typography>
                </Box>
                {countryId && configured && (
                    <Box sx={managementHeaderActionSx}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setEditing(null);
                                setModalOpen(true);
                            }}
                            sx={{ ...managementCreateButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { xs: '100%', md: 204 } }}
                        >
                            <Box component="span" sx={buttonContentSx}>
                                <Icon icon="tabler:plus" width="18" height="18" />
                                <span>ADD SEASON</span>
                            </Box>
                        </Button>
                    </Box>
                )}
            </Box>

            <Alert severity="info" sx={{ borderRadius: '16px', mb: 3 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>This changes how farmers are scored</AlertTitle>
                The engine resolves a farmer's season from this calendar. A country with none falls back to the
                built-in seasons — Kharif Jun–Oct, Zaid Apr–May, Rabi Nov–Mar — so clearing a calendar is a way
                to return to those, not a broken state. A rainfall baseline is keyed to one of these seasons.
            </Alert>

            <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
                <Box sx={{ maxWidth: 420 }}>
                    <FieldLabel label="Country" />
                    <TextField
                        select
                        fullWidth
                        value={countryId}
                        onChange={(event) => setCountryId(event.target.value)}
                        disabled={loading || countries.length === 0}
                        SelectProps={{ displayEmpty: true }}
                        sx={formFieldSx}
                    >
                        <MenuItem value="" disabled>
                            {loading ? 'Loading…' : 'Select a country'}
                        </MenuItem>
                        {countries.map((country) => (
                            <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
                        ))}
                    </TextField>
                    {countryNote ? (
                        <Typography sx={{ fontSize: 13, color: '#8A5A00', mt: 1 }}>{countryNote}</Typography>
                    ) : (
                        selectedCountry && (
                            <Typography sx={{ fontSize: 13, color: '#625B71', mt: 1 }}>
                                Applies to every {stateLevelLabel(selectedCountry).toLowerCase()} in{' '}
                                {selectedCountry.name}.
                            </Typography>
                        )
                    )}
                </Box>
            </Paper>

            {countryId && !configured && (
                <Paper elevation={0} sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
                    <Icon icon="tabler:calendar-off" width="40" height="40" color="#94A3B8" />
                    <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1C1B1F', mt: 2, mb: 1 }}>
                        No calendar for {selectedCountry?.name || 'this country'}
                    </Typography>
                    <Typography sx={{ color: '#625B71', maxWidth: 620, mx: 'auto', mb: 3 }}>
                        Rainfall baselines fall back to the engine's own three seasons until a calendar is set
                        here. Start from those and adjust, or build one from scratch.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button variant="contained" onClick={handleUseDefault} sx={{ ...managementCreateButtonSx, minWidth: 240 }}>
                            <Box component="span" sx={buttonContentSx}>
                                <Icon icon="tabler:copy" width="18" height="18" />
                                <span>START FROM {ENGINE_DEFAULT_SEASONS.length} DEFAULTS</span>
                            </Box>
                        </Button>
                        <Button
                            onClick={() => {
                                setEditing(null);
                                setModalOpen(true);
                            }}
                            sx={{ textTransform: 'none', color: '#294190', fontWeight: 600 }}
                        >
                            Add a season instead
                        </Button>
                    </Box>
                </Paper>
            )}

            {countryId && configured && (
                <>
                    <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
                        <Typography sx={captionSx}>Year coverage</Typography>
                        <Typography sx={{ color: '#625B71', fontSize: 14, mt: 0.5 }}>
                            Every month must belong to exactly one season, or a date resolves to none or to two.
                        </Typography>
                        <CoverageStrip seasons={seasons} indexByCode={indexByCode} />
                    </Paper>

                    {issues.length > 0 && (
                        <Alert severity="error" sx={{ borderRadius: '16px', mb: 3 }}>
                            <AlertTitle sx={{ fontWeight: 700 }}>This calendar does not cover the year</AlertTitle>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {issues.map((issue) => <li key={issue}>{issue}</li>)}
                            </Box>
                        </Alert>
                    )}

                    <Paper elevation={0} sx={{ ...cardSx, p: 0, overflow: 'hidden', mb: 3 }}>
                        {seasons.map((season, index) => (
                            <Box
                                key={season.code}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    px: 3,
                                    py: 2,
                                    borderBottom: index === seasons.length - 1 ? 'none' : '1px solid #EEF2F7',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box sx={{ width: 10, height: 40, borderRadius: '4px', backgroundColor: colorFor(index) }} />
                                <Box sx={{ minWidth: 200, flex: 1 }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1C1B1F' }}>
                                        {season.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: '#625B71', fontFamily: 'ui-monospace, monospace' }}>
                                        {season.code}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize: 15, color: '#1C1B1F', minWidth: 150 }}>
                                    {monthShort(season.startMonth)} – {monthShort(season.endMonth)}
                                    <Typography component="span" sx={{ fontSize: 13, color: '#625B71', ml: 1 }}>
                                        ({monthsCovered(season).length} mo)
                                    </Typography>
                                </Typography>
                                {spansNewYear(season) && (
                                    <Chip size="small" label="Crosses new year" sx={{ backgroundColor: '#FFF5E1', color: '#8A5A00' }} />
                                )}
                                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="Edit">
                                        <IconButton
                                            onClick={() => {
                                                setEditing(season);
                                                setModalOpen(true);
                                            }}
                                        >
                                            <EditOutlinedIcon sx={{ fontSize: 20, color: '#294190' }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove">
                                        <IconButton onClick={() => removeSeason(season.code)}>
                                            <DeleteOutlineIcon sx={{ fontSize: 20, color: '#DA342E' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        ))}
                    </Paper>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Button onClick={handleUseDefault} sx={{ textTransform: 'none', color: '#294190' }}>
                            Reset to engine defaults
                        </Button>
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                            {dirty && <Typography sx={{ fontSize: 14, color: '#8A5A00' }}>Unsaved changes</Typography>}
                            <Button
                                variant="contained"
                                disabled={!dirty}
                                onClick={handleSave}
                                sx={{ ...managementCreateButtonSx, minWidth: 180, height: 44 }}
                            >
                                SAVE CALENDAR
                            </Button>
                        </Box>
                    </Box>
                </>
            )}

            <SeasonModal
                open={modalOpen}
                season={editing}
                existing={seasons}
                onClose={() => {
                    setModalOpen(false);
                    setEditing(null);
                }}
                onSave={upsertSeason}
            />

            <NotificationSnackbar
                open={snackbar.open}
                onClose={closeSnackbar}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </>
    );
}

export default Seasons;
